import { store } from '@redux/store';
import { trackEvent, identifyUser, trackPageView } from 'middlewares/segment';
import SegmentEvent from 'dataTransferObjects/segmentEvent';
import SegmentIdentifyEvent from 'dataTransferObjects/segmentIdentifyEvent';
import SegmentEventType from 'enums/segmentEventType';
import {
  hasAvailableNow,
  hasReadyToBuild,
  hasInventoryStatusCombination,
  MoveInDate,
} from 'features/searchResultsFacets';
import getTrackingCommonProperties from 'features/trackingProperties';
import merge from 'deepmerge';
import cleanObjectsEmptyProperties from 'utils/cleanObject';
import DateExtensions from 'utils/dateExtensions';
import { FavoriteType, selectIsLoggedIn } from 'features/userProfile';
import SegmentPageEvent from 'dataTransferObjects/segmentPageEvent';
import LocationType from '../features/searchResultsFacets/enums/locationType';
import { getTrackerClientId } from './googleAnalytics';

/**
 * The getCommonProperties function returns an object containing the common properties
 * that are used in all Segment events.
 * @return An object
 */
const getCommonProperties = () => {
  const state = store.getState();
  const commonProperties = getTrackingCommonProperties(state);
  return commonProperties;
};

/**
 * The function filters the segment common properties, and take only the ones needed for Track events.
 * ex. track don't need user information. user information is available in common properties.
 * @returns object containing the common properties for a Track event.
 */
const getTrackCommonProperties = () => {
  const commonProperties = getCommonProperties();
  const marketNameProperty = (commonProperties.marketName && commonProperties.stateAbbreviation)
    ? `${commonProperties.marketName}, ${commonProperties.stateAbbreviation}`
    : undefined;

  const trackCommonProperties = {
    market_id: commonProperties.marketId?.toString(),
    market_name: marketNameProperty,
    city: commonProperties.city,
    state: commonProperties.stateAbbreviation,
    zip: commonProperties.zip,
    brand_id: commonProperties.brandId,
    brand_name: commonProperties.brandName,
    page_type: commonProperties.pageType,
    code_version: commonProperties.buildCodeVersion,
    test_version: commonProperties.testVersion,
    response_version: commonProperties.responseVersion,
    partner_id: commonProperties.partnerId,
    partner_name: commonProperties.partnerName,
    brand_partner_name_partner_name: commonProperties.brandPartnerName,
  };

  return trackCommonProperties;
};

/**
 * The createTrackEvent function is a helper function that creates an event object
 * for the Segment analytics library. The event object contains information about
 * the type of event, and any properties associated with it. This function returns
 * a redux-beacon middleware compatible action creator that can be dispatched to
 * trigger an analytics call to Segment's servers.
 * @param eventName Identify the event name
 * @param properties Pass in any additional properties that you want to be sent with the event
 *
 * @return A function that returns a segmentevent
 */
const createTrackEvent = ({ eventName, customProperties }) => {
  const commonProperties = getTrackCommonProperties();
  const properties = cleanObjectsEmptyProperties(merge(commonProperties, customProperties));
  return trackEvent(() => new SegmentEvent(eventName, properties));
};

/** method formats user favorites.
 * @returns An object with the list of Communities, Homes and Builders that are already saved.
 */
const getUserSavedElements = (favorites) => {
  const communities = [];
  const homes = [];
  const builders = [];

  favorites?.forEach((obj) => {
    const savedDate = DateExtensions.formatDateFromStringTimestamp(obj.dateChanged);
    const favoriteType = FavoriteType.fromKey(obj.type);
    // For the Community Saved Items
    if (favoriteType === FavoriteType.Community) {
      communities.push({
        communityId: obj.listingId,
        dateSaved: savedDate,
      });

      // For the Spec and Plan Saved Items
    } else if (favoriteType === FavoriteType.Spec || favoriteType === FavoriteType.Plan) {
      homes.push({
        homeId: obj.listingId,
        dateSaved: savedDate,
      });

      // For the Builder Saved Items
    } else if (favoriteType === FavoriteType.Builder) {
      builders.push({
        builderId: obj.builderInfo?.builderId,
        dateSaved: savedDate,
      });
    }
  });

  return {
    communities,
    homes,
    builders,
  };
};

/**
 * Checks the type and array length
 * @param array - the array to be checked.
 * @returns the array send on the parameters or null value if it's length is 0.
 */
const checkEmptyArray = (array) => {
  if (Array.isArray(array) && array.length) {
    return array;
  }
  return null;
};

/**
 * The function filters the segment identify common properties, and take only the ones needed for Identify events.
 * ex. track don't need user information. user information is available in common properties.
 * @returns object containing the common properties for a Track event.
 */
const getIdentifyProperties = () => {
  const state = store.getState();
  const commonProperties = getCommonProperties(state);
  const fullName = commonProperties.name?.split(' ');
  const savedElements = getUserSavedElements(commonProperties.favorites);
  const userId = commonProperties.userId?.replace(/[{}]/g, '');

  const identifyCommonProperties = {
    user_id: selectIsLoggedIn(state) ? userId : undefined, // set only if logged in
    email: commonProperties.email || null,
    zip: commonProperties.userZip || null,
    phone: commonProperties.phone || null,
    outside_us: commonProperties.livesOutside,
    saved_communities: checkEmptyArray(savedElements.communities),
    saved_homes: checkEmptyArray(savedElements.homes),
    saved_builders: checkEmptyArray(savedElements.builders),
    g_client_id: getTrackerClientId(),
    ...commonProperties.acxiomTrackingProperties
  };

  if (fullName && fullName.length > 0) {
    identifyCommonProperties.first_name = fullName[0] || null;
    identifyCommonProperties.last_name = fullName[1] || null;
  }

  return identifyCommonProperties;
};

const createIdentifyEvent = ({ eventName, customProperties }) => {
  const traits = cleanObjectsEmptyProperties(merge(getIdentifyProperties(), customProperties || {}));
  traits.last_event = eventName;
  const userId = traits.user_id;

  return identifyUser(() => new SegmentIdentifyEvent(userId, traits));
};

/**
 * getMoveInDate: Sets the Move-In value depending on the selection on the dropdown
 * @param moveInInformation The move-in information that can be either the date range or home status
 * @returns The value with the move-in home status or range.
 */
function getMoveInDate(moveInInformation) {
  const { moveInDateRange, homeStatus } = moveInInformation;
  /* return value for each QMI status */
  const moveNow = 'QMI_Now';
  const buildablePlans = 'R';
  const quickMoveIn = 'QMI';
  const oneToTwoMonths = 'QMI_1_2Months';
  const threeToSixMonths = 'QMI_3_6Months';

  /* All Quick Move-In */
  if (hasInventoryStatusCombination(homeStatus)) {
    return quickMoveIn;
  }

  /* Move now */
  if (hasAvailableNow(homeStatus)) {
    return moveNow;
  }

  /* Buildable Plans */
  if (hasReadyToBuild(homeStatus)) {
    return buildablePlans;
  }

  /* 1-2 months */
  if (moveInDateRange === MoveInDate.OneToTwoMonths.toString()) {
    return oneToTwoMonths;
  }

  /* 3-6 months */
  if (moveInDateRange === MoveInDate.ThreeToSixMonths.toString()) {
    return threeToSixMonths;
  }

  return undefined;
}

/**
 * getSearchLocation: Gets the search location information based on what's on the search box
 * @param locationInformation All the location information: market, state, zip
 * @returns The value with search location information.
 */
function getSearchLocation(locationInformation) {
  const { locationType, marketName, city, marketState, zip, syntheticGeographicName, county } = locationInformation;
  const searchType = LocationType.fromKey(locationType);

  /* Search label depending on the different search types */
  const searchLocationLabelDictionary = {
    market: `${marketName} ${marketState}, Area`,
    city: `${city}, ${marketState}`,
    zip,
    syntheticGeo: `${syntheticGeographicName}, ${marketState}`,
    county: `${county} County, ${marketState}`,
  };

  if (locationType === searchType.name) {
    return searchLocationLabelDictionary[locationType];
  }

  return undefined;
}

/**
 * getMasterBedLocationLabel: Gets the primary bedroom location: Up - Down.
 * @param bedroom The primary bedroom location: 1 / 2
 * @returns The master bed location that as requested should be: Up as 2 and Down as 1.
 */
function getMasterBedLocationLabel(bedroom) {
  switch (bedroom) {
    /* 1 value corresponds as second floor, but returns 2 since that was de value requested on ticket NHS20-2248 */
    case 1:
      return 2;
    /* 2 value corresponds as first floor, but returns 1 since that was de value requested on ticket NHS20-2248 */
    case 2:
      return 1;
    default:
      return undefined;
  }
}

/**  This function handles the mapping of the object values in order to rename them
 * as requested with underscore.
 * This function will receive as parameters all the filters facets applied and as well
 * the price if is min or max.
 */
function formatFiltersApplied(facets, price, schools, moveIn, search, locationType) {
  return {
    bathrooms: facets.bathrooms,
    bedrooms: facets.bedrooms,
    price_high: price?.maximumPrice,
    price_low: price?.minimumPrice,
    single_family: facets.singleFamily,
    model_homes: facets.modelHomes,
    luxury_homes: facets.luxury,
    condo_townhome: facets.condosAndTownHomes,
    living_areas: facets.livingAreas,
    master_bed_location: getMasterBedLocationLabel(facets.primaryBedroom),
    garages: facets.garages,
    stories: facets.stories,
    gated: facets.gatedCommunity,
    pool: facets.pool,
    golf_course: facets.golfCourse,
    green: facets.green,
    nature_areas: facets.natureAreas,
    parks: facets.parks,
    sports: facets.sportsFacilities,
    views: facets.views,
    waterfront: facets.waterfront,
    hot_deals: facets.hotDeals,
    price_reduction: facets.priceReduction,
    listings_hoa_info: facets.hasHomeOwnersAssociationFee,
    age_restricted: facets.adult,
    rv_garage: facets.rvGarage,
    school_district_ids: schools?.join(', '),
    brand_id: facets.builderBrand,
    move_in_date: getMoveInDate(moveIn),
    mfr: facets.manufactured,
    custom: facets.customHomes,
    search_location: getSearchLocation(search),
    coming_soon: facets.comingsoon,
    grand_opening: facets.grandOpening,
    active: facets.active,
    closeout: facets.closeOut,
    typeahead_search: locationType,
  };
}

/**
 * facetsEntriesValues: Gets the active values from facets and converts them into an object
 * filters when an object is active or not.
 * @param facet The facet we are trying to filter and convert
 * @returns The active facet values as an object.
 */
function facetsEntriesValues(facet) {
  const activeFacet = Object.entries(facet).filter(([, facetValue]) => Boolean(facetValue));

  const facetValues = Object.fromEntries(activeFacet);

  return facetValues;
}

/**
 * getImpressionBaseProperties: Gets all the only impression-shared properties to be used
 * on the impression events. It relates exactly to the common properties that are shown
 * on the impression events but not considering the track common properties.
 * @returns The properties that are shared for the impression events.
 */
const getImpressionBaseProperties = () => {
  const {
    facets,
    listingCount,
    pageNumber,
    price,
    schools,
    moveInDate,
    searchLocation,
    locationType,
  } = getCommonProperties();

  const filtersValues = facetsEntriesValues(facets);

  const priceValues = facetsEntriesValues(price);

  const impressionCommonProperties = {
    filters_applied: formatFiltersApplied(
      filtersValues,
      priceValues,
      schools,
      moveInDate,
      searchLocation,
      locationType
    ),
    result_count: listingCount,
    current_page_number: pageNumber,
  };
  return impressionCommonProperties;
};

/**
 * getImpressionProperties: Gets all the shared properties that should be used on
 * every impression event. It includes the track common properties.
 * @returns The impression properties that every impression event should use.
 */
const getImpressionProperties = () => {
  const trackCommonProperties = getTrackCommonProperties();
  const impressionCommonProperties = getImpressionBaseProperties();
  const properties = merge(trackCommonProperties, impressionCommonProperties);
  return properties;
};

/**
 * createImpressionEvent: Triggers the impression events with all the required properties.
 * @param eventName Identify the event name
 * @param customProperties Pass in any additional properties that you want to be sent with the event
 * @return A function that returns a segmentevent
 */
const createImpressionEvent = ({ eventName, customProperties }) => {
  const impressionProperties = getImpressionProperties();
  const properties =  cleanObjectsEmptyProperties(merge(impressionProperties, customProperties));
  return trackEvent(() => new SegmentEvent(eventName, properties));
};

const createPageEvent = ({ category, name, customProperties = {}, options }) => {
  const commonProperties = getTrackCommonProperties();
  const properties =  cleanObjectsEmptyProperties(merge(commonProperties, customProperties));
  return trackPageView(() => new SegmentPageEvent(category, name, properties, options));
};

/**
 * The getSegmentEvent function returns a SegmentEvent object.
 * @param eventType Determine which type of event to create
 * @param event Identify the event name, custom properties of the event.
 *
 * @return An SegmentEvent object
 */
const getSegmentEvent = (eventType, event) => {
  switch (eventType) {
    case SegmentEventType.Track:
      return createTrackEvent(event);
    case SegmentEventType.Identify:
      return createIdentifyEvent(event);
    case SegmentEventType.Impression:
      return createImpressionEvent(event);
    case SegmentEventType.PageView:
      return createPageEvent(event);
    default:
      return null;
  }
};

export default getSegmentEvent;
